import { useState, useCallback } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CircularProgress } from '../CircularProgress';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import apiClient from '../../../lib/api/client';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  KPICell, SectionFrame, performanceColor,
  type PeriodContentProps, type DashboardFilterScope, buildOtdSplit } from './shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { useUserPermissions } from '../../../contexts/UserPermissionsContext';
import {
  CurrentWeekDrilldownModal,
  type CurrentWeekDrilldownMode,
} from './CurrentWeekDrilldownModal';
import { buildCurrentWeekParams } from './currentWeekDrilldownParams';
import {
  CurrentWeekNCRDrilldownModal,
  type NCRDrilldownMode,
} from './CurrentWeekNCRDrilldownModal';
import { YTDDrilldownModal } from './YTDDrilldownModal';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

/** Map a weekday short label (Sun..Sat) to an ISO date inside the
 *  current week. ``window_start`` is the **Monday** of the current week
 *  (see ``_monday_of`` in scheduled_dashboard_service). Mon=0, Sun=6.
 *  Saturday and Sunday belong to the END of the same week. */
const DAY_OFFSETS: Record<string, number> = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
};
function dayIsoFor(weekStartIso: string, day: string): string {
  const offset = DAY_OFFSETS[day] ?? 0;
  const d = new Date(weekStartIso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

/** Default filter scope when ``filters`` is undefined (e.g. unit
 *  tests). The shell threads it through ScheduledTab in production. */
const DEFAULT_FILTERS: DashboardFilterScope = { erp_order_type: '4' };

export function CurrentWeek({ data, formatCurrency, filters }: PeriodContentProps) {
  const cw = data.current_week;
  const qualityLabel = useQualityMeasureLabel();
  // A user whose sites all have the Quality module off has no NCR/PPM data
  // to read and no Quality tab to drill into — the card is dead weight (and
  // its drilldowns 403). Hide it and let Delivery/Cost take the full row.
  const { hasModuleAccess } = useUserPermissions();
  const showQuality = hasModuleAccess('quality');
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision (customer review 2026-07). 0 keeps the historical whole-number
  // ring labels; 1-2 shows e.g. 96.2%.
  const { preferences } = usePreferences();
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0;
  const scope = filters ?? DEFAULT_FILTERS;

  // Mirror the gating used by DefaultTab / SiteKPITab so the customer's
  // PerformanceSettings.delivery_metric_mode controls whether OTIF, OTD,
  // or both blocks appear here too.
  // CR-6543 r2: tenant-configured targets (Admin Setup) — was hardcoded 95.
  const otifTarget = data.otif_target ?? 95;
  const otdTarget = data.otd_target ?? 95;
  const deliveryMode = data.delivery_metric_mode ?? 'both';
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only';
  const showOTD  = deliveryMode === 'both' || deliveryMode === 'otd_only';

  // When the week selector points at a past (complete) week, the panel shows
  // the whole Mon-Sun week rather than the live "Sun → today" / Thursday-frozen
  // window — so the subtitles/captions switch to the completed-week wording.
  const isLive = cw.is_live ?? true;
  const periodWord = isLive ? 'this' : 'that';

  const weekStartIso = cw.window_start;
  const weekEndIso   = cw.window_end;
  const otifStartIso = cw.otif_window_start;
  const otifEndIso   = cw.otif_window_end;
  const todayIso     = data.today;

  const [scheduleMode, setScheduleMode] = useState<CurrentWeekDrilldownMode | null>(null);
  const [scheduleDayIso, setScheduleDayIso] = useState<string | undefined>(undefined);
  const [ncrMode, setNcrMode] = useState<NCRDrilldownMode | null>(null);
  const [appealsOpen, setAppealsOpen] = useState(false);

  const openSchedule = useCallback((mode: CurrentWeekDrilldownMode, dayIso?: string) => {
    setScheduleDayIso(dayIso);
    setScheduleMode(mode);
  }, []);
  const closeSchedule = useCallback(() => {
    setScheduleMode(null);
    setScheduleDayIso(undefined);
  }, []);
  const openNcr = useCallback((mode: NCRDrilldownMode) => setNcrMode(mode), []);
  const closeNcr = useCallback(() => setNcrMode(null), []);

  // Export every scheduled delivery for the week as one Excel sheet, one row
  // per delivery with a leading Day-of-week column (grouped/ordered by day).
  // Reuses the "due this week" param builder so it honours the same dashboard
  // scope (supplier / part / customer / erp_order_type) and week window.
  const [exportingByDay, setExportingByDay] = useState(false);
  const handleExportByDay = useCallback(async () => {
    setExportingByDay(true);
    try {
      const params = {
        ...buildCurrentWeekParams({
          mode: 'due_this_week', filters: scope,
          weekStartIso, weekEndIso,
          otifStartIso, otifEndIso, todayIso,
        }),
        ordering: 'schedule_date,id',
      };
      const qs = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const resp = await apiClient.get(
        `/purchasing/schedules/export-deliveries-by-day-xlsx/?${qs}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deliveries_by_day_${weekStartIso}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Deliveries-by-day export ready');
    } catch (err: unknown) {
      console.error('Deliveries-by-day export failed:', err);
      toast.error('Failed to export deliveries by day');
    } finally {
      setExportingByDay(false);
    }
  }, [scope, weekStartIso, weekEndIso, otifStartIso, otifEndIso, todayIso]);

  const deliveriesByDay = cw.deliveries_by_day.map((d) => ({
    day: d.day,
    onTime: d.on_time,
    early: d.early,
    late: d.late,
    pending: d.pending,
  }));

  // M4 (wave 3b, FIX-05): every GRN in the population, unjudged ones included.
  const otdSplit = buildOtdSplit(cw.delivery);
  const otdEmpty = otdSplit.every(d => d.value === 0);
  const otdData: Array<{ name: string; value: number; color: string; mode: CurrentWeekDrilldownMode | null }> = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otdSplit;

  // OTIF Mix — three outcomes only:
  //   Passed  = ``passed``  (delivered in full within OTIF tolerance)
  //   Pending = ``pending`` (still within tolerance window, supplier
  //                          still has time)
  //   Failed  = ``failed``  (past window without passing, OR delivered
  //                          but didn't meet OTIF — short or out of
  //                          window). No "On-time short" / "Late in-full"
  //                          slices — both collapse into Failed.
  const otifPass    = cw.delivery.passed  ?? 0;
  const otifPending = cw.delivery.pending ?? 0;
  const otifFail    = cw.delivery.failed  ?? 0;
  const otifSplit = [
    { name: 'Passed',  value: otifPass,    color: PALETTE.onTime, mode: 'mix_otif_pass'    as const },
    { name: 'Pending', value: otifPending, color: '#94a3b8',      mode: 'mix_otif_pending' as const },
    { name: 'Failed',  value: otifFail,    color: '#dc2626',      mode: 'mix_otif_fail'    as const },
  ];
  const otifEmpty = otifSplit.every(d => d.value === 0);
  const otifData: Array<{ name: string; value: number; color: string; mode: CurrentWeekDrilldownMode | null }> = otifEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otifSplit;

  // Per-segment click handler. Recharts ``BarChart.onClick`` fires once
  // for the whole chart and gives ``activePayload[0]`` (always the first
  // series), so it can't distinguish which segment of a stack was
  // clicked. Wire ``<Bar onClick>`` on each Bar instead — the payload is
  // the data row (so we can read ``day``) and we already know the
  // dataKey/segment at the call site.
  const onSegmentClick = (segment: 'onTime' | 'early' | 'late' | 'pending') => (raw: unknown) => {
    const dayRaw = raw && typeof raw === 'object'
      ? (raw as { payload?: { day?: unknown }; day?: unknown }).day
        ?? (raw as { payload?: { day?: unknown } }).payload?.day
      : undefined;
    if (typeof dayRaw !== 'string') return;
    const iso = dayIsoFor(weekStartIso, dayRaw);
    if (segment === 'onTime')      openSchedule('day_on_time', iso);
    else if (segment === 'early')   openSchedule('day_early',   iso);
    else if (segment === 'late')    openSchedule('day_late',    iso);
    else if (segment === 'pending') openSchedule('day_pending', iso);
  };

  const handleSliceClick = (slice: unknown) => {
    if (!slice || typeof slice !== 'object') return;
    const m = (slice as { mode?: unknown }).mode;
    if (typeof m === 'string') openSchedule(m as CurrentWeekDrilldownMode);
  };

  return (
    <div className="space-y-6">
      <div className={`grid gap-4 ${showQuality ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {showQuality && (
          <SectionFrame title="Quality" accent="red" subtitle={isLive ? 'Sun → today' : 'full week'}>
            <div className="grid grid-cols-2 gap-3">
              <KPICell
                label={qualityLabel}
                value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ppm.toLocaleString()}</span>}
                foot={`${cw.quality.reject_qty.toLocaleString()} rej`}
                onClick={() => openNcr('ppm')}
              />
              <KPICell
                label="NCRs"
                value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ncrs.toLocaleString()}</span>}
                onClick={() => openNcr('ncrs')}
              />
            </div>
          </SectionFrame>
        )}

        <SectionFrame title="Delivery" accent="green" subtitle={!isLive ? 'full week' : (cw.frozen ? 'Frozen at Thu 17:00' : 'Sun → today')}>
          <div
            className={`grid gap-3 items-start ${
              showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'
            }`}
          >
            {showOTIF && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={cw.delivery.otif_pct} size={80} strokeWidth={6} color={performanceColor(cw.delivery.otif_pct, otifTarget)} decimals={kpiDecimals} />}
                foot={`${cw.delivery.otif_met}/${cw.delivery.total_due} schedules`}
                onClick={() => openSchedule('otif_this_week')}
              />
            )}
            {showOTD && (
              <KPICell
                label="OTD"
                value={<CircularProgress percentage={cw.delivery.otd_pct} size={80} strokeWidth={6} color={performanceColor(cw.delivery.otd_pct, otdTarget)} decimals={kpiDecimals} />}
                foot={`${cw.delivery.otd_met}/${cw.delivery.otd_due ?? cw.delivery.total_due} within firm`}
                onClick={() => openSchedule('otd_this_week')}
              />
            )}
            <KPICell
              label="Appeals"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.delivery.appeals}</span>}
              onClick={() => setAppealsOpen(true)}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle={`schedules due ${periodWord} week`}>
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Schedule Lines"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.open_value)}</span>}
              foot={`${cw.cost.open_lines} of ${cw.cost.total_lines} lines`}
              onClick={() => openSchedule('open_this_week')}
            />
            <KPICell
              label="Received"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cw.cost.total_value)} committed`}
              onClick={() => openSchedule('received_this_week')}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.arrears_value)}</span>}
              foot={`${cw.cost.arrears_lines} past-due lines`}
              onClick={() => openSchedule('arrears')}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Deliveries by Day + OTD Mix + OTIF Mix.
          Layout collapses to fewer columns when OTD or OTIF is hidden by the
          delivery_metric_mode setting. */}
      <div className={`grid gap-4 ${
        showOTIF && showOTD ? 'grid-cols-3'
        : (showOTIF || showOTD) ? 'grid-cols-2'
        : 'grid-cols-1'
      }`}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Day</CardTitle>
                <p className="text-[11px] text-muted-foreground">Sunday through Saturday • {periodWord} week — click a segment to drill in</p>
              </div>
              <Button
                variant="outline" size="sm" className="h-7 shrink-0"
                onClick={handleExportByDay} disabled={exportingByDay}
                title="Export every delivery this week to Excel, one row per delivery with a Day column"
              >
                {exportingByDay ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesByDay} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  {/* Darker hover cursor + activeBar outline so the user sees
                      the whole day's column light up before clicking a
                      segment. */}
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime} name="On time" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('onTime')} />
                  <Bar dataKey="early"   stackId="a" fill={PALETTE.early}  name="Early"   cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('early')} />
                  <Bar dataKey="late"    stackId="a" fill={PALETTE.late}   name="Late"    cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('late')} />
                  <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]} cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('pending')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {showOTD && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">{isLive ? 'Week to date' : 'Full week'} • {(cw.delivery.total_grns ?? cw.delivery.total_with_status).toLocaleString()} deliveries — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={otdData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={otdEmpty ? 0 : 2}
                      stroke="none"
                      onClick={handleSliceClick}
                      activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}
                    >
                      {otdData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {showOTIF && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTIF Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">{isLive ? 'This week' : 'That week'} • {(otifPass + otifPending + otifFail).toLocaleString()} schedules — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={otifData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={otifEmpty ? 0 : 2}
                      stroke="none"
                      onClick={handleSliceClick}
                      activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}
                    >
                      {otifData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {scheduleMode && (
        <CurrentWeekDrilldownModal
          open={scheduleMode !== null}
          onClose={closeSchedule}
          mode={scheduleMode}
          filters={scope}
          weekStartIso={weekStartIso}
          weekEndIso={weekEndIso}
          otifWindowStartIso={otifStartIso}
          otifWindowEndIso={otifEndIso}
          todayIso={todayIso}
          dayIso={scheduleDayIso}
          arrearsBufferDays={data.arrears_buffer_days ?? 0}
        />
      )}
      {ncrMode && (
        <CurrentWeekNCRDrilldownModal
          open={ncrMode !== null}
          onClose={closeNcr}
          mode={ncrMode}
          filters={scope}
          weekStartIso={weekStartIso}
          weekEndIso={weekEndIso}
        />
      )}

      <YTDDrilldownModal
        open={appealsOpen}
        onClose={() => setAppealsOpen(false)}
        mode="delivery_appeals"
        filters={scope}
        periodStart={weekStartIso}
        periodEnd={weekEndIso}
        todayIso={todayIso}
        periodLabel="This week"
      />
    </div>
  );
}
